import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, Car, MoreVertical, X } from "lucide-react";
import API_BASE_URL from "../api";

// ⚠️ Adjust to match your real backend route.
const API_BASE = `${API_BASE_URL}/api/inputHistory`;

const FUEL_OPTIONS = ["All Fuel Types", "Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const PAGE_SIZE = 10;

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [fuelFilter, setFuelFilter] = useState("All Fuel Types");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      setError(null);
      try {
        // ⚠️ Placeholder endpoint — replace with your real "list all vehicles" route.
        const res = await fetch(`${API_BASE}/totalVehicleRegistered`).then((r) => r.json());
        console.log("res", res)
        setVehicles(res?.vehicles || []);
      } catch (err) {
        console.error("Failed to load vehicles:", err);
        setError("Couldn't load vehicles. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  const filtered = useMemo(() => {
    return vehicles.filter((v) => {
      const matchesSearch =
        !search ||
        v.vehicleNumber?.toLowerCase().includes(search.toLowerCase()) ||
        v.ownerName?.toLowerCase().includes(search.toLowerCase()) ||
        v.model?.toLowerCase().includes(search.toLowerCase());
      const matchesFuel = fuelFilter === "All Fuel Types" || v.fuelType === fuelFilter;
      return matchesSearch && matchesFuel;
    });
  }, [vehicles, search, fuelFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, fuelFilter]);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-bold text-gray-900">Vehicles</h3>
        <p className="text-sm text-gray-500">
          {loading ? "Loading…" : `${filtered.length} vehicle${filtered.length !== 1 ? "s" : ""} registered`}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by vehicle no, owner, or model…"
            className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="relative">
          <select
            value={fuelFilter}
            onChange={(e) => setFuelFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 text-sm rounded-xl border border-gray-200 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
          >
            {FUEL_OPTIONS.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {error && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs">
                <th className="px-6 py-3 font-medium">Vehicle No.</th>
                <th className="px-4 py-3 font-medium">Model</th>
                <th className="px-4 py-3 font-medium">Fuel Type</th>
                <th className="px-4 py-3 font-medium">Owner Name</th>
                <th className="px-4 py-3 font-medium">Registered On</th>
                <th className="px-4 py-3 font-medium">Certificates Issued</th>
                <th className="px-6 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-gray-400">
                    Loading vehicles…
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <Car size={24} className="text-gray-300" />
                      No vehicles match your filters
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((row, i) => (
                  <tr
                    key={row.vehicleNumber || i}
                    className={i !== paginated.length - 1 ? "border-b border-gray-50" : ""}
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">{row.vehicleNumber || "—"}</td>
                    <td className="px-4 py-4 text-gray-600">{row.model || "—"}</td>
                    <td className="px-4 py-4 text-gray-700">{row.fuelType || "—"}</td>
                    <td className="px-4 py-4 text-gray-700">{row.ownerName || "—"}</td>
                    <td className="px-4 py-4 text-gray-500">
                      {row.registeredOn ? new Date(row.registeredOn).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-4 text-gray-700">{row.certificatesIssued ?? 0}</td>
                    <td className="px-6 py-4 text-right">
                      <MoreVertical size={16} className="cursor-pointer text-gray-400 hover:text-gray-600 inline-block" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-50 text-sm text-gray-500">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Prev
              </button>
              <span className="text-gray-400 text-xs">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}